import { create } from 'zustand';

/** A typing frame is sent at most once a second; without a new one the indicator fades. */
export const TYPING_TTL_MS = 4_000;

export interface Typist {
  /** `visitor` or the staff member's user id. */
  key: string;
  authorType: 'visitor' | 'staff';
  name: string | null;
  internal: boolean;
  /** Sneak peek: the shopper's draft, only when the store relays it. Never stored server-side. */
  draft: string | null;
  until: number;
}

interface TypingState {
  byConversation: Record<string, Typist[]>;
  set: (conversationId: string, typist: Typist) => void;
  clear: (conversationId: string, key: string) => void;
  prune: (now: number) => void;
}

export const useTypingStore = create<TypingState>()((set) => ({
  byConversation: {},
  set: (conversationId, typist) =>
    set((state) => ({
      byConversation: {
        ...state.byConversation,
        [conversationId]: [
          ...(state.byConversation[conversationId] ?? []).filter(
            (entry) => entry.key !== typist.key,
          ),
          typist,
        ],
      },
    })),
  clear: (conversationId, key) =>
    set((state) => {
      const current = state.byConversation[conversationId];
      if (!current?.some((entry) => entry.key === key)) return state;
      return {
        byConversation: {
          ...state.byConversation,
          [conversationId]: current.filter((entry) => entry.key !== key),
        },
      };
    }),
  prune: (now) =>
    set((state) => {
      let changed = false;
      const next: Record<string, Typist[]> = {};
      for (const [id, entries] of Object.entries(state.byConversation)) {
        const alive = entries.filter((entry) => entry.until > now);
        if (alive.length !== entries.length) changed = true;
        if (alive.length > 0) next[id] = alive;
      }
      return changed ? { byConversation: next } : state;
    }),
}));

const NONE: Typist[] = [];

export function useTypists(conversationId: string): Typist[] {
  return useTypingStore((state) => state.byConversation[conversationId] ?? NONE);
}
